// The delivery address on the pallet desk (pack and confirm screens): the
// lines the carrier's label will carry, the phone and email, and — while
// the order is still ready, nothing booked at a carrier yet — an Edit that
// corrects it in place (2026-11-03: the desk could only look at a wrong
// address; the fix had to be made on the Orders page). Saved through the
// Orders header PATCH the parcel desk uses, so the same phone / country
// rules apply; the carrier gets whatever is saved here — Palletways on
// Book, JAS / Raben on Ship.
import { useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { EU_COUNTRY_OPTIONS, EU_PHONE_FORMATS, dialCodeForCountry } from "../helpers/phoneFormat";
import { addressLines, phoneDisplay, type DeskDelivery, type DeskOrder } from "./palletDesk";

interface Props {
    order: DeskOrder;
    /** Edit is offered only while the order is ready (no booking at the carrier) and the desk is not read-only. */
    editable: boolean;
    busy: boolean;
    /** Wraps the save so the page's one-at-a-time lock applies. */
    run: (action: () => Promise<void>) => void;
    /** The saved address, so the open screen shows it before the desk reloads. */
    onDelivery: (delivery: DeskDelivery) => void;
    /** Something changed on the order — reload the desk. */
    onChanged: () => Promise<void>;
}

type Form = Record<"name" | "company" | "line_1" | "line_2" | "city" | "county" | "postcode" | "country" | "phone_country" | "phone" | "email", string>;

const toForm = (d: DeskDelivery): Form => ({
    name: d.name ?? "", company: d.company ?? "", line_1: d.line_1 ?? "", line_2: d.line_2 ?? "",
    city: d.city ?? "", county: d.county ?? "", postcode: d.postcode ?? "", country: (d.country ?? "").toUpperCase(),
    // No dial code on record: offer the country's, as the Orders page does.
    phone_country: d.phone_country ?? dialCodeForCountry(d.country) ?? "", phone: d.phone ?? "", email: d.email ?? "",
});

const blank = (v: string): string | null => (v === "" ? null : v);

const PalletAddress = ({ order, editable, busy, run, onDelivery, onChanged }: Props) => {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState<Form>(() => toForm(order.delivery));
    const phone = phoneDisplay(order.delivery);
    const set = (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const open = () => { setForm(toForm(order.delivery)); setEditing(true); };
    const close = () => setEditing(false);

    // A country outside the EU / GB list (the order came in with it) stays
    // choosable rather than being silently replaced.
    const countries = form.country && !EU_COUNTRY_OPTIONS.some((c) => c.code === form.country)
        ? [{ code: form.country, name: form.country, dialCode: "" }, ...EU_COUNTRY_OPTIONS]
        : EU_COUNTRY_OPTIONS;

    const save = (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        const f = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()])) as Form;
        run(async () => {
            const address = {
                name: f.name, company: f.company, line_1: f.line_1, line_2: f.line_2,
                city: f.city, county: f.county, postcode: f.postcode, country: f.country.toUpperCase(),
                phone_country: f.phone_country || null, phone: f.phone, email: f.email,
            };
            const res = await apiFetch(`/orders/${encodeURIComponent(order.order_id)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ delivery_address: address }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                playError();
                toast.error(body?.message || t("server_error"), { toastId: "pallet-address-save" });
                return;
            }
            playSuccess();
            toast.success(t("address_updated"), { toastId: `pallet-address-${order.order_id}` });
            onDelivery({
                ...order.delivery,
                name: blank(address.name), company: blank(address.company), line_1: blank(address.line_1), line_2: blank(address.line_2),
                city: blank(address.city), county: blank(address.county), postcode: blank(address.postcode), country: blank(address.country),
                phone: blank(address.phone), phone_country: address.phone ? address.phone_country : null, email: blank(address.email),
            });
            setEditing(false);
            await onChanged();
        });
    };

    if (editing) {
        return (
            <form className="pallet-address pallet-address--editing dispatch-address-form" onSubmit={save} aria-label={t("delivery_address")}>
                <label className="dispatch-edit-label" htmlFor="pallet-address-name">{t("name")}</label>
                <input id="pallet-address-name" value={form.name} onChange={set("name")} required autoFocus />
                <label className="dispatch-edit-label" htmlFor="pallet-address-company">{t("company")}</label>
                <input id="pallet-address-company" value={form.company} onChange={set("company")} />
                <label className="dispatch-edit-label" htmlFor="pallet-address-line-1">{t("address_line_1")}</label>
                <input id="pallet-address-line-1" value={form.line_1} onChange={set("line_1")} required />
                <label className="dispatch-edit-label" htmlFor="pallet-address-line-2">{t("address_line_2")}</label>
                <input id="pallet-address-line-2" value={form.line_2} onChange={set("line_2")} />
                <div className="pallet-address-row">
                    <div>
                        <label className="dispatch-edit-label" htmlFor="pallet-address-city">{t("city")}</label>
                        <input id="pallet-address-city" value={form.city} onChange={set("city")} required />
                    </div>
                    <div>
                        <label className="dispatch-edit-label" htmlFor="pallet-address-county">{t("county")}</label>
                        <input id="pallet-address-county" value={form.county} onChange={set("county")} />
                    </div>
                </div>
                <div className="pallet-address-row">
                    <div>
                        <label className="dispatch-edit-label" htmlFor="pallet-address-postcode">{t("postcode")}</label>
                        <input id="pallet-address-postcode" value={form.postcode} onChange={set("postcode")} required />
                    </div>
                    <div>
                        <label className="dispatch-edit-label" htmlFor="pallet-address-country">{t("country")}</label>
                        <select id="pallet-address-country" value={form.country} onChange={set("country")} required>
                            <option value="">—</option>
                            {countries.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                        </select>
                    </div>
                </div>
                <div className="pallet-address-row">
                    <div>
                        <label className="dispatch-edit-label" htmlFor="pallet-address-phone">{t("phone")}</label>
                        <div className="dispatch-phone-input-group pallet-address-phone">
                            <select className="dispatch-phone-dial-select" value={form.phone_country} onChange={set("phone_country")} aria-label={t("phone_country_code")} title={t("phone_country_code")}>
                                <option value="">—</option>
                                {EU_COUNTRY_OPTIONS.map((c) => <option key={c.code} value={c.dialCode}>{c.dialCode}</option>)}
                            </select>
                            <input id="pallet-address-phone" className="dispatch-phone-national" value={form.phone} onChange={set("phone")}
                                placeholder={EU_PHONE_FORMATS[form.country]?.mobileExample || t("phone")} type="tel" inputMode="numeric" />
                        </div>
                    </div>
                    <div>
                        <label className="dispatch-edit-label" htmlFor="pallet-address-email">{t("email")}</label>
                        <input id="pallet-address-email" value={form.email} onChange={set("email")} type="email" />
                    </div>
                </div>
                <div className="pallet-address-actions">
                    <button type="submit" className="btn btn-primary" disabled={busy}>
                        <Check size={16} aria-hidden="true" /> {t("save")}
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={close} disabled={busy}>
                        <X size={16} aria-hidden="true" /> {t("cancel")}
                    </button>
                </div>
            </form>
        );
    }

    return (
        <div className="pallet-address">
            <address className="pallet-screen-address">
                {addressLines(order.delivery).map((line, i) => <span key={i}>{line}</span>)}
                {order.delivery.country && <span>{order.delivery.country}</span>}
                <span className="pallet-dispatch-contact">
                    {phone || t("pallet_dispatch_no_phone")} · {order.delivery.email || t("pallet_dispatch_no_email")}
                </span>
            </address>
            {editable && (
                <button type="button" className="btn btn-secondary pallet-address-edit" onClick={open} disabled={busy} aria-label={`${t("edit")} ${t("delivery_address")}`}>
                    <Pencil size={16} aria-hidden="true" /> {t("edit")}
                </button>
            )}
        </div>
    );
};

export default PalletAddress;
